/** Page batch size for very large books only. */
export const READER_PAGE_WINDOW = 30;

/** Books with more pages than this use sliding-window loading. */
export const LARGE_BOOK_THRESHOLD = 100;

export function mergePagesData(existing = [], incoming = []) {
  const map = new Map((existing || []).map((p) => [p.page_num, p]));
  (incoming || []).forEach((p) => map.set(p.page_num, p));
  return [...map.values()].sort((a, b) => a.page_num - b.page_num);
}

export function mergePagesContent(existing = [], incoming = []) {
  const map = new Map((existing || []).map((p) => [p.page, p]));
  (incoming || []).forEach((p) => map.set(p.page, p));
  return [...map.values()].sort((a, b) => a.page - b.page);
}

export function pageDataFor(book, pageNum) {
  return book?.pages_data?.find((p) => p.page_num === pageNum - 1);
}

export function isPdfImageBook(book) {
  if (!book) return false;
  if (book.render_mode === 'image') return true;
  return Array.isArray(book.pages_data) && book.pages_data.some((p) => p?.image_url);
}

export function bookPageTotal(book) {
  const fromPages = Number(book?.pages) || 0;
  const fromTotal = Number(book?.total_pages) || 0;
  const fromData = book?.pages_data?.length || 0;
  return Math.max(fromPages, fromTotal, fromData);
}

export function normalizeBookPages(data) {
  const total = bookPageTotal(data);
  return { ...data, pages: total, total_pages: total };
}

export function windowForPage(pageNum, total, windowSize = READER_PAGE_WINDOW) {
  const size = Math.min(windowSize, total);
  const start = Math.max(0, Math.min(pageNum - 1 - Math.floor(size / 2), total - size));
  const end = Math.min(total, start + size);
  return { start, end };
}

export function isPageLoaded(book, pageNum) {
  const pd = pageDataFor(book, pageNum);
  return !!(pd && pd.image_url);
}

export function needsPageWindow(book, pageNum) {
  const total = bookPageTotal(book);
  if (total <= 0 || !isPdfImageBook(book)) return false;
  if (pageNum > total) return false;
  return !isPageLoaded(book, pageNum);
}

export function hasMissingPages(book) {
  if (!isPdfImageBook(book)) return false;
  const total = bookPageTotal(book);
  if (total <= 0) return false;
  for (let pageNum = 1; pageNum <= total; pageNum += 1) {
    if (!isPageLoaded(book, pageNum)) return true;
  }
  return false;
}

export async function fetchBookPageWindow(apiBase, bookId, token, start, end) {
  const res = await fetch(
    `${apiBase}/books/${bookId}?page_start=${start}&page_end=${end}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!res.ok) throw new Error(`Failed to fetch book pages (${res.status})`);
  return res.json();
}

/** Fetch every missing page image descriptor for typical-length books. */
export async function fetchAllMissingPages(apiBase, bookId, token, book) {
  const total = bookPageTotal(book);
  if (!isPdfImageBook(book) || total <= 0 || !hasMissingPages(book)) {
    return book;
  }

  const rangeData = await fetchBookPageWindow(apiBase, bookId, token, 0, total);
  return normalizeBookPages({
    ...book,
    pages_data: mergePagesData(book.pages_data, rangeData.pages_data),
    pages_content: mergePagesContent(
      book.pages_content,
      rangeData.pages_content || []
    ),
    page_window: { start: 0, end: total },
    has_more_pages: false,
  });
}

/** Load book metadata plus page data for the reader's starting position. */
export async function fetchBookForReader(apiBase, bookId, token) {
  const headers = { Authorization: `Bearer ${token}` };
  const res = await fetch(`${apiBase}/books/${bookId}`, { headers });
  if (!res.ok) throw new Error('Failed to fetch book');
  let data = await res.json();
  data = normalizeBookPages(data);

  if (isPdfImageBook(data) && hasMissingPages(data)) {
    data = await fetchAllMissingPages(apiBase, bookId, token, data);
  }

  return normalizeBookPages(data);
}

export async function ensurePageWindowLoaded(apiBase, bookId, token, book, pageNum) {
  if (!needsPageWindow(book, pageNum)) return book;

  const total = bookPageTotal(book);
  if (total <= LARGE_BOOK_THRESHOLD) {
    return fetchAllMissingPages(apiBase, bookId, token, book);
  }

  const { start, end } = windowForPage(pageNum, total);
  const rangeData = await fetchBookPageWindow(apiBase, bookId, token, start, end);
  let updated = normalizeBookPages({
    ...book,
    pages_data: mergePagesData(book.pages_data, rangeData.pages_data),
    pages_content: mergePagesContent(
      book.pages_content,
      rangeData.pages_content || []
    ),
    page_window: rangeData.page_window || rangeData.page_range || { start, end },
    has_more_pages: true,
  });

  if (needsPageWindow(updated, pageNum)) {
    updated = await fetchAllMissingPages(apiBase, bookId, token, updated);
  }

  return updated;
}
